// src/components/memberships/BuyCreditsModal.tsx
import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Alert } from "../common/information/Alert";
import { ButtonFluxi } from "../common/Buttons/ButtonFluxi/ButtonFluxi";
import { convertToZone } from "../../utils/formatters";
import { format } from "date-fns";
import { handleApi } from "../../utils/handleApi";
import { createCreditPurchaseLink, getCreditPrice } from "../../services/payment/Credits";
import toast from "react-hot-toast";
import { Language } from "../../utils/dictionary";
import { dictionary } from "../../utils/dictionary";
interface BuyCreditsModalProps {
  onClose: () => void;
  nextRenewalDate: string;
  language: Language;
}

export const BuyCreditsModal = ({ onClose, nextRenewalDate, language }: BuyCreditsModalProps) => {
  const [packs, setPacks] = useState(1);
  const [pricePerPack, setPricePerPack] = useState(750);
  const [creditsPerPack, setCreditsPerPack] = useState(150);
  const [currency, setCurrency] = useState("USD");
  const totalCredits = packs * creditsPerPack;
  const totalPrice = (packs * pricePerPack) / 100;
  const formatDate = "dd/MM/yyyy";

  useEffect(() => {
    const fetchCreditPrice = async () => {
      const { res } = await handleApi(getCreditPrice());
      if (res) {
        setPricePerPack(res.price_per_pack || 750);
        setCreditsPerPack(res.credits_per_pack || 150);
        setCurrency(res.currency || "USD");
      }
    };
    fetchCreditPrice();
  }, []);


  const nextRenewalDateFormatted = convertToZone(
    format(new Date(nextRenewalDate || ""), formatDate),
    formatDate
  );

  const handleIncrement = () => setPacks(prev => prev + 1);
  const handleDecrement = () => setPacks(prev => Math.max(1, prev - 1));

  const handleBuy = async () => {
    const { res } = await handleApi(createCreditPurchaseLink(packs));
    if (res && res.payment_url) {
      window.location.href = res.payment_url;
      onClose();
    } else {
        toast.error(dictionary["error-creating-purchase-link"][language]);
    }
  };

  return (
    <div className="p-6">
      {/* Título */}
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        {dictionary["buy-credits"][language]}
      </h2>
      <p className="text-gray-500 mb-6">
        {dictionary["select-credits"][language]}
      </p>

      {/* Selector de cantidad */}
      <div className="mb-6">
        <label className="block text-gray-700 font-medium mb-4 text-center">
          {dictionary["credits-quantity"]?.[language] || "Cantidad de créditos"}
        </label>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={handleDecrement}
            className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <Minus className="w-6 h-6 text-gray-700" />
          </button>

          <div className="w-32 h-14 border-2 border-gray-200 rounded-lg flex items-center justify-center">
            <span className="text-4xl font-bold text-gray-900">{totalCredits}</span>
          </div>

          <button
            onClick={handleIncrement}
            className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <Plus className="w-6 h-6 text-gray-700" />
          </button>
        </div>
      </div>

      {/* Total */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-gray-900">Total:</span>
          <span className="text-3xl font-bold text-gray-900">${totalPrice.toFixed(2)} {currency}</span>
        </div>
      </div>

      {/* Alerta */}
      <div className="mb-6">
        <Alert
          type="warning"
          description={
            <>
              Los créditos que compres se vencerán el <strong className="font-bold">{nextRenewalDateFormatted}</strong>. Asegúrate de usarlos antes de esa fecha.
            </>
          }
        />
      </div>

      {/* Botón de compra */}
      <ButtonFluxi
        text={dictionary["pay-now"][language]}
        onClick={handleBuy}
        fullWidth
        style={{ fontSize: "18px" }}
      />
    </div>
  );
};